import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Grid,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  Pill,
} from 'web-library';
import { useTranslation } from 'react-i18next';
import { FormControlLabel } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import { MutationStatus } from 'react-query';
import { TFunction } from 'i18next';
import CandidateSearch from '../../../../components/search/CandidateSearch';
import * as QUERIES from '../../../../api/candidate/resit/@opadmin/queries-services/queries';
import { OopAdminResitSearchCandidateResponse } from '../../../../api/candidate/resit/@opadmin/api-services/models';
import { CommonErrorResponse, GenericErrorResponse } from '../../../../api';
import HandleErrors from '../../../../components/HandleErrors';
import { usePostSendUniqueLinkEmail } from '../../../../api-refactored/unique-link/usePostSendUniqueLinkEmail';
import { useGetUserAuthorities } from '../../../../api-refactored/user/useGetUserAuthorities';

/** define MUI Data Table options
 * */
const tableOptions: MUIDataTableOptions = {
  rowsPerPageOptions: [1],
  elevation: 0,
  filter: false,
  confirmFilters: false,
  download: false,
  print: false,
  viewColumns: false,
  selectableRows: 'none',
  fixedHeader: true,
  responsive: 'standard',
  customToolbarSelect: () => <div />,
};

/** define Mui DataTable Column Definition
 * */
const tableColumns: MUIDataTableColumnDef[] = [
  {
    name: 'name',
    label: 'Candidate Name',
    options: {
      sort: false,
      searchable: true,
    },
  },
  {
    name: 'candidateId',
    label: 'Candidate NRIC',
    options: {
      sort: false,
      searchable: true,
    },
  },
  {
    name: 'email',
    label: 'Candidate Email',
    options: {
      sort: false,
      searchable: true,
    },
  },
];

export interface CandidateDetails extends OopAdminResitSearchCandidateResponse {
  bankTransfer: boolean;
}

interface SendEmailProps {
  sendEmailLinkStatus: MutationStatus;
  hasCreate: boolean;
  dataIndex: number;
  onSendEmail: (number: number) => void;
  t: TFunction;
}

const SendEmail = ({
  sendEmailLinkStatus,
  hasCreate,
  dataIndex,
  onSendEmail,
  t,
}: SendEmailProps) => (
  <div
    style={{
      marginLeft: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      minWidth: 85,
      minHeight: 26,
    }}
  >
    {(sendEmailLinkStatus === 'idle' || sendEmailLinkStatus === 'error') && hasCreate && (
      <Button
        variant="text"
        id="button-sendemail"
        onClick={() => {
          onSendEmail(dataIndex);
        }}
        size="small"
      >
        {t('resit->>OpAdminResit.Send Email').toString()}
      </Button>
    )}
    {sendEmailLinkStatus === 'loading' && (
      <CircularProgress size={20} style={{ position: 'absolute' }} />
    )}
    {sendEmailLinkStatus === 'success' && <Pill label="Email Sent" size="S" variant="positive" />}
  </div>
);

const CandidateResitRegistration = () => {
  const [candidateData, setCandidateData] = useState<CandidateDetails[]>([]);
  const [responseErrors, setResponseErrors] = useState<
    CommonErrorResponse | GenericErrorResponse
  >();
  const [hasCreate, setHasCreate] = useState<boolean>(false);
  const { t } = useTranslation();

  const { data: authorities = [] } = useGetUserAuthorities();

  useEffect(() => {
    if (authorities.includes(AUTHORITY_NAMES.CP_ADMIN_GRANT_RESIT_UNIQUE_LINK_CREATE)) {
      setHasCreate(true);
    }
  }, [authorities]);

  /** search candidate by filter
   * * */
  const { mutate, status } = QUERIES.useOopAdminSearchCandidate(
    (response) => {
      if (response && response.data) {
        setCandidateData([{ ...response.data, bankTransfer: false }]);
      }
    },
    (errors) => {
      if (errors.response && errors.response?.status !== 500) {
        setCandidateData([]);
        setResponseErrors(errors.response?.data);
      }
    }
  );

  /** send unique email link
   * */
  const {
    mutate: sendEmailLink,
    status: sendEmailLinkStatus,
    reset,
  } = usePostSendUniqueLinkEmail();

  /** handle send email action
   * */
  const onSendEmail = useCallback(
    (currentDataIndex: number) => {
      sendEmailLink({
        uuid: candidateData[currentDataIndex].uuid,
        userType: 'CANDIDATES',
        applicationType: 'RESIT',
        isWaiveSubjectAdminFee: false,
        isWaiveLateFee: false,
        isOfflinePayment: candidateData[currentDataIndex].bankTransfer,
      });
    },
    [sendEmailLink, candidateData]
  );

  /** handle candidate search
   * */
  const onCandidateSearch = (candidateFilter: string) => {
    reset();
    setResponseErrors(undefined);
    mutate({ candidateId: candidateFilter });
  };

  const BankTransferCheckbox = useCallback(
    (dataIndex: number) => (
      <div style={{ marginLeft: 8 }}>
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              value={candidateData[dataIndex].bankTransfer}
              name="oop-admin-resit-candidate-banktransfer"
              onChange={(event) => {
                setCandidateData([
                  { ...candidateData[dataIndex], bankTransfer: event.target.checked },
                ]);
              }}
              disabled={sendEmailLinkStatus === 'success'}
            />
          }
          label="Bank Transfer"
        />
      </div>
    ),
    [candidateData, sendEmailLinkStatus]
  );

  /** add custom columns
   * */
  const getTableColumnOptions = useMemo<MUIDataTableColumnDef[]>(() => {
    /** add bank transfer option column
     * */
    const addBankTransferActionColumn: MUIDataTableColumnDef = {
      options: {
        customBodyRenderLite: (dataIndex: number, _rowIndex: number) => BankTransferCheckbox(dataIndex),
        searchable: false,
        sort: false,
      },
      name: 'bankTransfer',
      label: 'Bank Transfer',
    };
    /** send email action column
     * */
    const addSendEmailActionColumn: MUIDataTableColumnDef = {
      name: 'action',
      label: 'Send Email',
      options: {
        sort: false,
        searchable: false,
        customBodyRenderLite: (dataIndex: number) =>
          SendEmail({
            sendEmailLinkStatus,
            hasCreate,
            dataIndex,
            onSendEmail,
            t,
          }),
      },
    };
    return [...tableColumns, addBankTransferActionColumn, addSendEmailActionColumn];
  }, [BankTransferCheckbox, sendEmailLinkStatus, hasCreate, onSendEmail, t]);

  return (
    <Grid container direction="column">
      <CandidateSearch
        onSearch={onCandidateSearch}
        clearErrors={() => setResponseErrors(undefined)}
        loading={status === 'loading'}
      />

      {responseErrors && <HandleErrors errorResponse={responseErrors} errorType="text" />}

      {!responseErrors && candidateData && candidateData.length > 0 && (
        <Grid xs={12}>
          <MUIDataTable
            key="candidate"
            columns={getTableColumnOptions}
            data={candidateData || [['Loading...']]}
            options={tableOptions}
          />
        </Grid>
      )}
    </Grid>
  );
};
export default CandidateResitRegistration;
